import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import { memo, useCallback, forwardRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useSnackbar } from 'notistack';
import Stack from '@mui/material/Stack';

import Button from '@/components/Button';
import Modal, { type ModalRef } from '@/components/Modal';
import { forgotPasswordSchema } from '@/domain/auth/schemas';
import { useForgotPassword } from '@/api/hooks/public/useForgotPassword';
import type { ForgotPasswordRequest } from '@/api/routes';
import { useTranslate } from '@/state/i18n';

interface Props {}

const ForgotPassword = forwardRef<ModalRef, Props>((_, ref) => {
  const t = useTranslate('domain.auth.components.ForgotPassword');
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordRequest>({
    resolver: joiResolver(forgotPasswordSchema),
    mode: 'onTouched',
  });

  const { mutateAsync: forgotPassword, isPending, isSuccess } = useForgotPassword();

  const onSubmit = useCallback(
    async (data: ForgotPasswordRequest) => {
      try {
        await forgotPassword(data);
        enqueueSnackbar(t('sent'), {
          variant: 'success',
        });
        // Close modal on success
        if (ref && 'current' in ref && ref.current) {
          ref.current.close();
        }
      } catch {}
    },
    [forgotPassword, ref, enqueueSnackbar, t],
  );

  const handleClose = useCallback(() => {
    if (ref && 'current' in ref && ref.current) {
      ref.current.close();
    }
  }, [ref]);

  return (
    <Modal
      ref={ref}
      title={t('title')}
      showClose
      actions={
        <>
          <Button type="button" onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={isPending || isSuccess}
            loading={isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {t('continue')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('body')}
        </Typography>
        <Stack direction="column" gap={0.5}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <OutlinedInput
                {...field}
                autoFocus
                required
                margin="dense"
                id="email"
                name="email"
                label={t('email')}
                placeholder={t('email')}
                type="email"
                fullWidth
                error={!!errors.email}
              />
            )}
          />
          {errors.email && (
            <Typography variant="caption" color="error">
              {errors.email.message}
            </Typography>
          )}
        </Stack>
      </form>
    </Modal>
  );
});

ForgotPassword.displayName = 'ForgotPassword';

export default memo(ForgotPassword);
